import { Link, type LinkProps, useRouterState } from '@tanstack/react-router'
import type { BreadcrumbProps } from 'antd'
import type { ReactNode } from 'react'
import React from 'react'

type AntdBreadcrumbItem = NonNullable<BreadcrumbProps['items']>[number]

type BreadcrumbValueObject = Omit<AntdBreadcrumbItem, 'href' | 'title'> &
  Partial<Pick<LinkProps, 'to' | 'params'>> & { label?: ReactNode; icon?: ReactNode }

export type BreadcrumbValue = string | BreadcrumbValueObject
export interface BreadcrumbLoaderData {
  breadcrumb?: BreadcrumbValue | BreadcrumbValue[]
}

export interface UseAppBreadcrumbsOptions {
  /** Make the last crumb a link instead of plain text */
  renderLastAsLink?: boolean
  /** When set, collapse middle crumbs by inserting an ellipsis (simple slice strategy) */
  maxItems?: number
}

/**
 * useAppBreadcrumbs: computes AntD Breadcrumb items from TanStack Router matches.
 * Each route can provide `{ breadcrumb: string | { label, icon, to, params, ... } }` or an array thereof in its loader result.
 * Only matches that provide a `breadcrumb` are included in the trail.
 */
export function useAppBreadcrumbs(options?: UseAppBreadcrumbsOptions): NonNullable<BreadcrumbProps['items']> {
  const { renderLastAsLink = true, maxItems = 4 } = options ?? {}

  return useRouterState({
    select: (s): NonNullable<BreadcrumbProps['items']> => {
      // Build crumbs from matches that expose loaderData.breadcrumb
      const crumbs = s.matches.flatMap(match => {
        const data = match.loaderData as BreadcrumbLoaderData | undefined
        if (!data || data.breadcrumb == null) return []

        const values = Array.isArray(data.breadcrumb) ? data.breadcrumb : [data.breadcrumb]

        return values.map((value, i) => {
          const normalized: BreadcrumbValueObject = typeof value === 'string' ? { label: value } : value

          const { label, icon, to, params, ...rest } = normalized

          return {
            key: `${match.id}${i > 0 ? `-${i}` : ''}`,
            to,
            params: params ?? (match.params as Record<string, string>),
            icon,
            label,
            rest,
          }
        })
      })

      // Map to AntD Breadcrumb items
      const baseItems = crumbs.map((m, index) => {
        const isLast = index === crumbs.length - 1
        const canLink = m.to !== undefined && (!isLast || !!renderLastAsLink)
        const titleNode = React.createElement(
          'span',
          { className: 'inline-flex items-center gap-2' },
          m.icon ?? null,
          React.createElement('span', null, m.label),
        )
        return {
          ...m.rest,
          key: m.rest.key ?? m.key,
          title: canLink ? React.createElement(Link, { to: m.to as never }, titleNode) : titleNode,
        }
      })

      if (!maxItems || baseItems.length <= maxItems) return baseItems

      // Simple middle collapse: keep first and last N while inserting an ellipsis
      const keepHead = Math.max(1, Math.floor(maxItems / 2))
      const tailCount = maxItems - keepHead - 1 // 1 for ellipsis
      if (tailCount <= 0) return baseItems

      return [
        ...baseItems.slice(0, keepHead),
        { key: 'ellipsis', title: '…' },
        ...baseItems.slice(baseItems.length - tailCount),
      ]
    },
  })
}
